import { Box } from '@mui/material';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@Components/Button';
import DateRangePicker from '@Components/DateRangePicker';
import MainSectionLayout from '@Components/MainSectionLayout';
import SearchBar from '@Components/SearchBar';
import Select from '@Components/Select';
import { DialerTypes } from '@Typings/global.types';

import ScriptsLayoutProps from './ScriptsLayout.types';

const ScriptsLayout: FunctionComponent<ScriptsLayoutProps> = (
  props: ScriptsLayoutProps
): JSX.Element => {
  const {
    isAdmin,
    scriptType,
    searchProps,
    selectFilterProps,
    dateRangeProps,
    heading,
    showFilterControls,
    selectedBtnNumber,
    selectedBtnProps,
    cancelBtnProps,
    deleteBtnProps,
    archiveUnarchiveBtnProps,
    clearFilterBtnProps,
    scriptsAddBtnProps,
    libraryAddBtnProps,
    ...resProps
  } = props;

  /** Translation Hook */
  const { t } = useTranslation();

  /** Page Heading */
  const headingText =
    scriptType === DialerTypes.Active
      ? t('scripts.Call Scripts')
      : t('scripts.Archived Call Scripts');

  /** Table Filters */
  const filtersComponent = (
    <>
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={1.6}
        mb={1.6}
      >
        <SearchBar {...searchProps} maxWidth={33.3} />
        <Select
          {...selectFilterProps}
          variant="secondary"
          sx={{
            minWidth: '22.5rem',
          }}
        />
        <Box minWidth="25.6rem">
          <DateRangePicker {...dateRangeProps} />
        </Box>
        {scriptType === DialerTypes.Active && (
          <Box display="flex" justifyContent="end" columnGap="15px">
            <Button
              color="primary"
              icon="add"
              text={t('scripts.Add Script')}
              sx={{ ml: 'auto' }}
              {...scriptsAddBtnProps}
            />
            {!isAdmin && (
              <Button
                color="primary"
                icon="add"
                text={t('scripts.Add from Library')}
                sx={{ ml: 'auto' }}
                {...libraryAddBtnProps}
              />
            )}
          </Box>
        )}
      </Box>
      {!!selectedBtnNumber && (
        <Box display="inline-flex" alignItems="center" gap={0.8}>
          <Button
            variant="text"
            color="inherit"
            icon="checkboxFilled"
            iconColor="primary"
            text={`${selectedBtnNumber || 0} ${t('common.messages.Selected')}`}
            sx={{ textTransform: 'none' }}
            {...selectedBtnProps}
          />
          <Button
            variant="text"
            color="inherit"
            icon="delete"
            text={t('common.messages.Delete')}
            sx={{ textTransform: 'none' }}
            {...deleteBtnProps}
          />
          {!isAdmin && (
            <Button
              variant="text"
              color="inherit"
              icon={
                scriptType === DialerTypes.Active
                  ? 'sendAndArchive'
                  : 'unarchive'
              }
              text={
                scriptType === DialerTypes.Active
                  ? t('common.messages.Archive')
                  : t('common.messages.Unarchive')
              }
              sx={{ textTransform: 'none' }}
              {...archiveUnarchiveBtnProps}
            />
          )}

          <Button
            variant="text"
            color="secondary"
            text={t('common.messages.Cancel')}
            {...cancelBtnProps}
          />
        </Box>
      )}
      {showFilterControls && (
        <Box
          display="inline-flex"
          alignItems="center"
          ml={selectedBtnNumber ? 0.8 : 0}
        >
          <Button
            variant="text"
            color="secondary"
            icon="close"
            text={t('common.tableFilters.Clear Filters')}
            sx={{ textTransform: 'none' }}
            {...clearFilterBtnProps}
          />
        </Box>
      )}
    </>
  );

  return (
    <MainSectionLayout
      heading={heading || headingText}
      filters={filtersComponent}
      {...resProps}
    />
  );
};

ScriptsLayout.defaultProps = {
  scriptType: DialerTypes.Active,
};

export default ScriptsLayout;
